import React from 'react';
import { GoogleMap } from '@react-google-maps/api';

import OrderArrow from '../CommonComponents/GoogleMap/OrderArrow';
import OrderMarker from '../CommonComponents/GoogleMap/OrderMarker';
import RoutePolygons from '../CommonComponents/GoogleMap/RoutePolygons';
import ApproximationCircle from '../CommonComponents/GoogleMap/ApproximationCircle';

import { useDispatch, useSelector } from '../../redux/hooks';
import {
    selectFilteredMarketEntities,
    selectMarketFilters,
    selectSelectedMarketEntity,
    setSelectedMarketEntity,
    selectMarketSettings
} from '../../redux/actions/market';
import { fetchAllWatchlists, selectAllWatchlists } from '../../redux/actions/watchlists';
import { circleRoutePoints } from '../../utils/tenantRoutes';
import { COLOR_BLUE } from '../../theme';
import useTenantRoutes from './useTenantRoutes';

const defaultCenter = { lat: 63, lng: 16 };
const defaultZoomLevel = 5;
// Enough corners for a circle to look round as a dashed outline
const CIRCLE_OUTLINE_POINTS = 72;
const options = {
    mapTypeControl: false,
    fullscreenControl: false,
    streetViewControl: false,
    clickableIcons: false,
    minZoom: 4,
    maxZoom: 20
};

// type SortOrderType = { DHL: number, CAP: number, LL: number };
// type SortObjectType = { type: 'DHL' | 'CAP' | 'LL' };
// function sortJobs(a: SortObjectType, b: SortObjectType) {
//     const order: SortOrderType = { DHL: 1, CAP: 2, LL: 3 };

//     if (order[a.type] < order[b.type]) {
//         return -1;
//     }
//     if (order[a.type] > order[b.type]) {
//         return 1;
//     }
//     return 0;
// }

function getBoundsZoomLevel(bounds: any, mapDim: any) {
    const WORLD_DIM = { height: 256, width: 256 };
    const ZOOM_MAX = 12;

    function latRad(lat: number) {
        const sin = Math.sin((lat * Math.PI) / 180);
        const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
        return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
    }

    function zoom(mapPx: number, worldPx: number, fraction: number) {
        return Math.floor(Math.log((mapPx / worldPx / fraction) / Math.LN2));
    }

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    const latFraction = (latRad(ne.lat()) - latRad(sw.lat())) / Math.PI;

    const lngDiff = ne.lng() - sw.lng();
    const lngFraction = ((lngDiff < 0) ? (lngDiff + 360) : lngDiff) / 360;

    const latZoom = zoom(mapDim.height, WORLD_DIM.height, latFraction);
    const lngZoom = zoom(mapDim.width, WORLD_DIM.width, lngFraction);

    const customAdjustment = 1;
    return Math.min(latZoom, lngZoom, ZOOM_MAX) + customAdjustment;
}

let mapClickTimeout: number | undefined;

function getZIndexFromEntityType({ type }: { type: 'LL' | 'DHL' | 'CAP' }) {
    if (type === 'CAP') {
        return 600;
    }
    if (type === 'LL') {
        return 500;
    }
    return undefined;
}

export default function MarketMap() {
    const dispatch = useDispatch();
    const entities = useSelector(selectFilteredMarketEntities());
    const selectedEntity = useSelector(selectSelectedMarketEntity());
    const jobFilters = useSelector(selectMarketFilters());
    const {
        autoFocus,
        showPickupPin,
        showDeliveryPin,
        showDistanceLine
    } = useSelector(selectMarketSettings());

    const tenantRoutes = useTenantRoutes();

    const [map, setMap] = React.useState<any>(null);

    // The route(s) the filter is limited to, drawn as outlines
    const {
        routeId, insideRoutes, watchlistId, placeLat, placeLng, placeRadius
    } = jobFilters;
    const shownRoutes = React.useMemo(() => (
        (tenantRoutes ?? [])
            .map((route) => ({ ...route, color: COLOR_BLUE }))
            .filter((route) => (routeId ? route.id === routeId : insideRoutes))
    ), [tenantRoutes, routeId, insideRoutes]);

    // The watchlist the filter is limited to: its company route, own polygon or circle is drawn like the routes
    const watchlists = useSelector(selectAllWatchlists());
    const watchlist = React.useMemo(() => (
        watchlistId ? watchlists.find((w) => String(w.id) === String(watchlistId)) ?? null : null
    ), [watchlists, watchlistId]);
    const watchlistListEmpty = watchlists.length === 0;
    React.useEffect(() => {
        if (watchlistId && watchlistListEmpty) {
            dispatch(fetchAllWatchlists());
        }
    }, [dispatch, watchlistId, watchlistListEmpty]);
    // Only a polygon: a watchlist's route or circle is filled in and drawn as the filter's own route or place
    const watchlistArea = React.useMemo(() => {
        if (!watchlist || routeId || placeLat !== null) return null;
        const { area } = watchlist;
        return area?.type === 'polygon' && area.shape.points.length >= 3
            ? { key: `p${watchlist.id}`, points: area.shape.points }
            : null;
    }, [watchlist, routeId, placeLat]);
    const watchlistRoutes = React.useMemo(() => {
        if (!watchlistArea) return [];
        return [{
            id: -1, name: '', points: watchlistArea.points, color: COLOR_BLUE
        }];
    }, [watchlistArea]);

    // The place with radius the filter is limited to
    const placeRoutes = React.useMemo(() => (
        placeLat !== null
            ? [{
                id: -2,
                name: '',
                points: circleRoutePoints(placeLat, placeLng, placeRadius, CIRCLE_OUTLINE_POINTS),
                color: COLOR_BLUE
            }]
            : []
    ), [placeLat, placeLng, placeRadius]);

    // Fit the map to the shown routes plus the plotted entities when either changes (debounced)
    const entitiesSignature = React.useMemo(() => entities.map((e: { key: string }) => e.key).join(','), [entities]);
    const fitData = {
        shownRoutes,
        watchlistArea,
        placeRoutes,
        entities,
        showPickupPin,
        showDeliveryPin,
        showDistanceLine
    };
    const fitRef = React.useRef(fitData);
    fitRef.current = fitData;
    const shownRoutesSignature = [
        ...shownRoutes.map((route) => route.id),
        watchlistArea?.key ?? '',
        placeLat !== null ? `${placeLat}:${placeLng}:${placeRadius}` : ''
    ].filter(Boolean).join(',');
    React.useEffect(() => {
        if (!map || !shownRoutesSignature) return undefined;
        const timer = window.setTimeout(() => {
            const { current } = fitRef;
            const bounds = new window.google.maps.LatLngBounds();
            let hasPoints = false;
            current.shownRoutes.forEach((route) => route.points.forEach((point) => {
                bounds.extend(point);
                hasPoints = true;
            }));
            current.watchlistArea?.points.forEach((point) => {
                bounds.extend(point);
                hasPoints = true;
            });
            current.placeRoutes.forEach((route) => route.points.forEach((point) => {
                bounds.extend(point);
                hasPoints = true;
            }));
            current.entities.forEach((entity: any) => {
                if (current.showPickupPin || current.showDistanceLine) {
                    bounds.extend({ lat: entity.pickupLat, lng: entity.pickupLng });
                    hasPoints = true;
                }
                if (current.showDeliveryPin || current.showDistanceLine) {
                    bounds.extend({ lat: entity.deliveryLat, lng: entity.deliveryLng });
                    hasPoints = true;
                }
            });
            if (hasPoints) {
                map.fitBounds(bounds, 48);
            }
        }, 400);
        return () => window.clearTimeout(timer);
    }, [map, shownRoutesSignature, entitiesSignature]);

    // OLD:
    // React.useEffect(() => {
    //     if (map) {
    //         const bounds = new window.google.maps.LatLngBounds();
    //         if (selectedEntity) {
    //             bounds.extend({ lat: selectedEntity.pickupLat, lng: selectedEntity.pickupLng });
    //             bounds.extend({ lat: selectedEntity.deliveryLat, lng: selectedEntity.deliveryLng });
    //         } else {
    //             entities.forEach((entity) => bounds.extend({ lat: entity.pickupLat, lng: entity.pickupLng }));
    //         }
    //         if (entities.length) {
    //             // Can adjust for offset to driveline here as well
    //             const { offsetHeight, offsetWidth } = map.getDiv();
    //             map.setZoom(getBoundsZoomLevel(bounds, { height: offsetHeight, width: offsetWidth }));
    //             map.panTo(bounds.getCenter());
    //             // map.fitBounds(bounds); // Does not pan (just jumps) if same zoom-level
    //         }
    //     }
    // }, [map, entities, selectedEntity]);

    React.useEffect(() => {
        // Only pan to pickup (do not zoom) if there is a selected entity
        if (autoFocus && selectedEntity && map) {
            const bounds = new window.google.maps.LatLngBounds();
            bounds.extend({ lat: selectedEntity.pickupLat, lng: selectedEntity.pickupLng });
            bounds.extend({ lat: selectedEntity.deliveryLat, lng: selectedEntity.deliveryLng });
            // Can adjust for offset to driveline here as well
            const { offsetHeight, offsetWidth } = map.getDiv();
            map.setZoom(getBoundsZoomLevel(bounds, { height: offsetHeight, width: offsetWidth }));
            map.panTo(bounds.getCenter());
            // map.fitBounds(bounds); // Does not pan (just jumps) if same zoom-level
            map.panBy(0, 100); // Offsetting due to the JobDescription at bottom
        }
    }, [autoFocus, entities, map, selectedEntity]);

    const onMapClick = React.useCallback(() => {
        if (selectedEntity) {
            mapClickTimeout = window.setTimeout(() => dispatch(setSelectedMarketEntity(null)), 200);
        }
    }, [dispatch, selectedEntity]);

    const onLoad = React.useCallback((newMap) => {
        // newMap.addListener('click', () => {
        //     // This should only run if entity is selected
        //     dispatch(setSelectedMarketEntity(null));
        // });
        // Allow zooming in by dblclick without deselecting selected marker
        newMap.addListener('dblclick', () => {
            window.clearTimeout(mapClickTimeout);
        });
        setMap(newMap);
    }, []);

    const onMarkerClick = React.useCallback((entity: { key: string }) => {
        dispatch(setSelectedMarketEntity(entity.key));
    }, [dispatch]);

    return (
        <GoogleMap
            mapContainerStyle={{ height: '100%', overflow: 'visible' }}
            center={defaultCenter}
            zoom={defaultZoomLevel}
            onLoad={onLoad}
            options={options}
            onClick={onMapClick}
        >
            <RoutePolygons routes={shownRoutes} fillOpacity={0.05} strokeWeight={2.5} dashed />
            <RoutePolygons routes={watchlistRoutes} fillOpacity={0.05} strokeWeight={2.5} dashed />
            <RoutePolygons routes={placeRoutes} fillOpacity={0.05} strokeWeight={2.5} dashed />

            {entities
                .filter((entity) => selectedEntity?.key !== entity.key)
                // .slice().sort(sortJobs)
                .map((entity) => (
                    <React.Fragment key={entity.key}>
                        {showPickupPin && (
                            <OrderMarker
                                entityType={entity.type}
                                markerType="plain"
                                lat={entity.pickupLat}
                                lng={entity.pickupLng}
                                onClick={() => onMarkerClick(entity)}
                                zIndex={getZIndexFromEntityType(entity)}
                            />
                        )}
                        {showDeliveryPin && (
                            <OrderMarker
                                entityType={entity.type}
                                markerType="plain"
                                lat={entity.deliveryLat}
                                lng={entity.deliveryLng}
                                onClick={() => onMarkerClick(entity)}
                                zIndex={getZIndexFromEntityType(entity)}
                            />
                        )}
                        {showDistanceLine && (
                            <OrderArrow
                                entityType={entity.type}
                                pickupLat={entity.pickupLat}
                                pickupLng={entity.pickupLng}
                                deliveryLat={entity.deliveryLat}
                                deliveryLng={entity.deliveryLng}
                                onClick={() => onMarkerClick(entity)}
                            />
                        )}
                    </React.Fragment>
                ))}

            {selectedEntity && (
                <>
                    <OrderMarker
                        entityType={selectedEntity.type}
                        markerType="activePickup"
                        lat={selectedEntity.pickupLat}
                        lng={selectedEntity.pickupLng}
                        zIndex={700}
                    />
                    <OrderMarker
                        entityType={selectedEntity.type}
                        markerType="activeDelivery"
                        lat={selectedEntity.deliveryLat}
                        lng={selectedEntity.deliveryLng}
                        zIndex={700}
                    />
                    <ApproximationCircle
                        lat={selectedEntity.pickupLat}
                        lng={selectedEntity.pickupLng}
                    />
                    <ApproximationCircle
                        lat={selectedEntity.deliveryLat}
                        lng={selectedEntity.deliveryLng}
                    />
                    <OrderArrow
                        entityType="ACTIVE"
                        isActive
                        pickupLat={selectedEntity.pickupLat}
                        pickupLng={selectedEntity.pickupLng}
                        deliveryLat={selectedEntity.deliveryLat}
                        deliveryLng={selectedEntity.deliveryLng}
                    />
                </>
            )}

        </GoogleMap>
    );
}
